import { License, licenseSchema } from "./License";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { makeRequired } from "../../../shared/components/forms/formUtils";
import { RhfForm } from "../../../shared/components/forms/RhfForm";
import { FormHeader } from "../../../shared/components/forms/FormHeader";
import { Box, Grid, Tab } from "@mui/material";
import { Application } from "../Application";
import { MuiRhfCompanySelectField } from "../../companies/MuiRhfCompanySelectField";
import { MuiRhfDatePicker } from "../../../shared/components/forms/MuiRhfDatePicker";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { ServerLicenseForm } from "./ServerLicenseForm";
import { QuotaLicensesForm } from "./QuotaLicensesForm";
import { UserQuotaLicenseForm } from "./UserQuotaLicenseForm";

export function LicenseForm(props: {
  application: Application,
  license: License,
  handleSave: (license: License) => void,
  handleDelete: (license: License) => void,
  handleClose: () => void,
}) {
  const { t } = useTranslation();

  const [ formReadonly, setFormReadonly ] = useState<boolean>(!!props.license.id);
  const [ license, setLicense ] = useState<License>(props.license);
  const [ selectedTab, setSelectedTab ] = React.useState('1');

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: license,
    resolver: yupResolver(licenseSchema(t))
  });
  const { reset, register } = methods;

  useEffect(() => {
    register('id');
  }, [ register ]);

  useEffect(() => {
    setLicense(props.license);
  }, [ props.license ]);

  useEffect(() => {
    reset(license);
  }, [ license, reset ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const required = makeRequired(licenseSchema(t));
  const isNew = !(!!license.id)
  const name = license.serverLicense?.name || license.quotaLicenses?.[0]?.name || "";
  const editFormTitle = isNew ? t('License.titleNew') : t('License.titleEdit') + ': ' + name;
  return (
    <FormProvider {...methods} >
      <RhfForm id={'license-form'} onSubmit={props.handleSave}>
        <FormHeader title={editFormTitle}
                    onCancel={props.handleClose}
                    onDelete={() => props.handleDelete(props.license)}
                    canBeEdited={true}
                    readonly={formReadonly}
                    onStartEditing={() => setFormReadonly(false)}
        />
        <Grid container={true} sx={{ margin: 2, width: 'auto' }}>
          <Grid item={true} xs={12} md={7} lg={8}>
            <Grid container={true} spacing={3}>
              <Grid item={true} xs={12} lg={6}>
                <MuiRhfCompanySelectField name={"company"} label={t("License.company")}
                                          required={required.company.__required}
                                          disabled={formReadonly} />
              </Grid>
              <Grid item={true} xs={12} lg={6}>
              </Grid>
              <Grid item={true} xs={6} lg={4}>
                <MuiRhfDatePicker name={'validityInfo.validFrom'} label={t('ValidityInfo.validFrom')}
                                  required={required.validityInfo.validFrom}
                                  disabled={formReadonly} />
              </Grid>
              <Grid item={true} xs={6} lg={4}>
                <MuiRhfDatePicker name={'validityInfo.validTo'} label={t('ValidityInfo.validTo')}
                                  required={required.validityInfo.validTo}
                                  disabled={formReadonly} />
              </Grid>
            </Grid>
          </Grid>
          <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', xs: 12, marginTop: 2 }}>
              <TabList onChange={handleTabChange} aria-label="lab API tabs example">
                <Tab label={t("License.serverLicense.title")} value="1" />
                <Tab label={t("License.quotaLicense.title")} value="2" />
                <Tab label={t("License.userQuotaLicense.title")} value="3" />
              </TabList>
            </Box>
            <TabPanel value="1" sx={{ width: "100%" }}>
              <ServerLicenseForm license={props.license} application={props.application} readOnly={formReadonly} />
            </TabPanel>
            <TabPanel value="2" sx={{ width: "100%" }}>
              <QuotaLicensesForm license={props.license} application={props.application} readOnly={formReadonly} />
            </TabPanel>
            <TabPanel value="3" sx={{ width: "100%" }}>
              <UserQuotaLicenseForm license={props.license} application={props.application} readOnly={formReadonly} />
            </TabPanel>
          </TabContext>
        </Grid>
      </RhfForm>
    </FormProvider>
  );
}
